import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import PdfViewer from 'components/PdfViewer';

interface DocumentEntry {
  fileName: string;
  url: string;
}

interface DocumentListProps {
  data: DocumentEntry[];
}

const Block = styled.div`
  grid-column: 1;
  grid-row: 1 / 3;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
`;

const Counter = styled.div`
  flex-shrink: 0;
  height: 48px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const CounterCurrent = styled.div`
  background: #3994de1a;
  border-radius: 4px;
  padding: 6px;
  font-size: 22px;
  font-weight: 500;
  color: #000;
  min-width: 28px;
  text-align: center;
`;

const CounterSeparator = styled.span`
  font-size: 22px;
  font-weight: 500;
  color: #000;
`;

const CounterTotal = styled.span`
  font-size: 22px;
  font-weight: 500;
  color: #000;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 32px;
`;

const DocSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0 0;
`;

const DocTitle = styled.div`
  font-size: 22px;
  font-weight: 500;
  color: #000;
  text-align: center;
  padding-bottom: 8px;
`;

export const DocumentList = ({ data }: DocumentListProps) => {
  const [currentDoc, setCurrentDoc] = useState(1);
  const titleRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data.length) return;

    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const handleScroll = () => {
      const threshold = 150;
      let current = 1;
      titleRefs.current.forEach((ref, i) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          if (rect.top <= threshold) {
            current = i + 1;
          }
        }
      });
      setCurrentDoc(current);
    };

    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [data.length]);

  return (
    <Block>
      <Counter>
        <CounterCurrent>{currentDoc}</CounterCurrent>
        <CounterSeparator>/</CounterSeparator>
        <CounterTotal>{data.length}</CounterTotal>
      </Counter>
      <ScrollArea ref={scrollRef}>
        {data.map((doc, i) => (
          <DocSection key={i}>
            <DocTitle ref={(el: HTMLDivElement | null) => (titleRefs.current[i] = el)}>{doc.fileName}</DocTitle>
            <PdfViewer url={doc.url} />
          </DocSection>
        ))}
      </ScrollArea>
    </Block>
  );
};
